/**
 * Location Query Keys - TanStack Query
 * 
 * Centraliza todas las query keys para locations, warehouses y ubicaciones.
 * Esto permite invalidación de cache más precisa y organizada.
 * 
 * Estructura de keys:
 * - locations (root)
 *   - warehouses
 *     - list (con filtros)
 *     - detail (por id)
 *   - ubicaciones
 *     - list (con filtros)
 *     - tree (estructura jerárquica)
 *     - detail (por id)
 *   - types
 *     - list (tipos de locations)
 * 
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 */

import { 
  LocationFilterModel, 
  LocationTypeFilterModel,
  CountryFilterModel,
  StateCountryFilterModel,
  CityFilterModel,
} from '@kanzi-apes/kanzi-models';

/**
 * Interfaz para filtros de ubicaciones (extiende LocationFilterModel)
 */
export interface UbicacionFilterModel extends LocationFilterModel {
  type__name?: string;           // Filtrar por tipo específico
  type__name__ne?: string;       // Excluir tipo específico
  parent_id?: number;            // Filtrar por padre
  level?: number;                // Filtrar por nivel jerárquico
  search?: string;               // Búsqueda por nombre o external_id
}

/**
 * Query Keys centralizadas para el módulo de locations
 */
export const locationsQueryKeys = {
  /**
   * Key raíz para todas las queries de locations
   */
  all: ['locations'] as const,

  // ==================== WAREHOUSES ====================

  /**
   * Key base para todas las queries de warehouses
   */
  warehouses: () => [...locationsQueryKeys.all, 'warehouses'] as const,

  /**
   * Key para lista de warehouses con filtros opcionales
   * @param filters - Filtros de búsqueda
   */
  warehousesList: (filters: LocationFilterModel | null = null) =>
    [...locationsQueryKeys.warehouses(), 'list', filters] as const,

  /**
   * Key para detalle de un warehouse específico
   * @param id - ID del warehouse
   */
  warehouseDetail: (id: number) =>
    [...locationsQueryKeys.warehouses(), 'detail', id] as const,

  // ==================== STORES ====================

  /**
   * Key base para todas las queries de stores
   */
  stores: () => [...locationsQueryKeys.all, 'stores'] as const,

  /**
   * Key para lista de stores con filtros opcionales
   * @param filters - Filtros de búsqueda
   */
  storesList: (filters: LocationFilterModel | null = null) =>
    [...locationsQueryKeys.stores(), 'list', filters] as const,

  /**
   * Key para detalle de un store específico
   * @param id - ID del store
   */
  storeDetail: (id: number) =>
    [...locationsQueryKeys.stores(), 'detail', id] as const,

  // ==================== UBICACIONES ====================

  /**
   * Key base para todas las queries de ubicaciones
   */
  ubicaciones: () => [...locationsQueryKeys.all, 'ubicaciones'] as const,

  /**
   * Key para lista de ubicaciones con filtros opcionales
   * @param filters - Filtros de búsqueda
   */
  ubicacionesList: (filters: UbicacionFilterModel | null = null) =>
    [...locationsQueryKeys.ubicaciones(), 'list', filters] as const,

  /**
   * Key para estructura de árbol de ubicaciones
   * @param filters - Filtros de búsqueda (incluye type_id para filtrar por tipo padre)
   */
  ubicacionesTree: (filters: UbicacionFilterModel | null = null) =>
    [...locationsQueryKeys.ubicaciones(), 'tree', filters] as const,

  /**
   * Key para estructura de árbol de ubicaciones hijas (sin exclusión de tipo)
   * @param filters - Filtros de búsqueda (tipicamente solo parent_id)
   */
  childLocationsTree: (filters: UbicacionFilterModel | null = null) =>
    [...locationsQueryKeys.ubicaciones(), 'children-tree', filters] as const,

  /**
   * Key para lista paginada de hijos directos por parent_id (drill-down).
   * A diferencia de childLocationsTree, devuelve una lista plana paginada del nivel actual
   * (sin traer el árbol completo) y sin asumir el tipo del padre ni de los hijos.
   * @param filters - Filtros de búsqueda (parent_id o ausencia de parent_id para la raíz)
   */
  ubicacionesChildren: (filters: UbicacionFilterModel | null = null) =>
    [...locationsQueryKeys.ubicaciones(), 'children', filters] as const,

  /**
   * Key para detalle de una ubicación específica
   * @param id - ID de la ubicación
   */
  ubicacionDetail: (id: number) =>
    [...locationsQueryKeys.ubicaciones(), 'detail', id] as const,

  // ==================== LOCATION TYPES ====================

  /**
   * Key base para todas las queries de tipos de locations
   */
  types: () => [...locationsQueryKeys.all, 'types'] as const,

  /**
   * Key para lista de tipos de locations con filtros opcionales
   * @param filters - Filtros de búsqueda
   */
  typesList: (filters: LocationTypeFilterModel | null = null) =>
    [...locationsQueryKeys.types(), 'list', filters] as const,

  // ==================== GEOGRAPHIC DATA ====================

  /**
   * Key base para todas las queries de países
   */
  countries: () => [...locationsQueryKeys.all, 'countries'] as const,

  /**
   * Key para lista de países con filtros opcionales
   * @param filters - Filtros de búsqueda
   */
  countriesList: (filters: CountryFilterModel | null = null) =>
    [...locationsQueryKeys.countries(), 'list', filters] as const,

  /**
   * Key base para todas las queries de estados/provincias
   */
  states: () => [...locationsQueryKeys.all, 'states'] as const,

  /**
   * Key para lista de estados con filtros opcionales
   * @param filters - Filtros de búsqueda (típicamente country_id)
   */
  statesList: (filters: StateCountryFilterModel | null = null) =>
    [...locationsQueryKeys.states(), 'list', filters] as const,

  /**
   * Key base para todas las queries de ciudades
   */
  cities: () => [...locationsQueryKeys.all, 'cities'] as const,

  /**
   * Key para lista de ciudades con filtros opcionales
   * @param filters - Filtros de búsqueda (típicamente state_id)
   */
  citiesList: (filters: CityFilterModel | null = null) =>
    [...locationsQueryKeys.cities(), 'list', filters] as const,
};

/**
 * Helper: Remover propiedades undefined de un objeto
 * Evita enviar parámetros undefined al API
 */
function cleanFilters<T extends Record<string, any>>(filters: T | null): T {
  if (!filters) return {} as T;
  
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  }
  return cleaned as T;
}

/**
 * Helper: Construir filtros sin agregar exclusiones de tipo.
 * Útil para obtener ubicaciones hijas de una locación sin restricciones de tipo.
 * Solo limpia propiedades undefined/null.
 */
export function buildChildLocationFilters(
  filters: UbicacionFilterModel | null
): UbicacionFilterModel {
  return cleanFilters(filters);
}

/**
 * Helper: Construir filtros para warehouses
 * Agrega automáticamente el filtro type__name = 'WAREHOUSE'
 * y elimina propiedades undefined
 */
export function buildWarehouseFilters(
  filters: LocationFilterModel | null
): LocationFilterModel {
  const cleaned = cleanFilters(filters);
  return {
    ...cleaned,
    type__name: 'WAREHOUSE',
  };
}

/**
 * Helper: Construir filtros para stores
 * Agrega automáticamente el filtro type__name = 'STORE'
 * y elimina propiedades undefined
 */
export function buildStoreFilters(
  filters: LocationFilterModel | null
): LocationFilterModel {
  const cleaned = cleanFilters(filters);
  return {
    ...cleaned,
    type__name: 'STORE',
  };
}

/**
 * Helper: Construir filtros para ubicaciones
 * Agrega automáticamente el filtro type__name__ne = 'WAREHOUSE'
 * y elimina propiedades undefined
 */
export function buildUbicacionFilters(
  filters: UbicacionFilterModel | null
): UbicacionFilterModel {
  const cleaned = cleanFilters(filters);
  return {
    ...cleaned,
    type__name__ne: 'WAREHOUSE',
  };
}
